import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Zap, FolderPlus, Play, CreditCard, Video, TrendingUp, ArrowRight, Clock, Mail, AlertTriangle, Youtube, Upload, UserCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendingEmail, setResendingEmail] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (projectId) => {
    try {
      const res = await api.post(`/projects/${projectId}/generate-video`);
      const videoId = res.data.video_id || res.data.id;
      toast.success('Generation lancee ! Suivez la progression dans Historique.', { duration: 5000 });
      // Navigate to history after 2s for visual feedback
      setTimeout(() => navigate('/history'), 2000);
      loadStats();
      return videoId;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de generation');
    }
  };

  const handlePublishYouTube = async (projectId) => {
    try {
      toast.info('Publication YouTube en cours...');
      const res = await api.post(`/projects/${projectId}/publish-youtube`);
      toast.success(`Publie sur YouTube ! ${res.data.youtube_url}`);
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de publication');
    }
  };

  const handleResendConfirmation = async () => {
    setResendingEmail(true);
    try {
      await api.post('/auth/resend-confirmation');
      toast.success('Email de confirmation renvoye !');
    } catch {
      toast.error('Erreur lors du renvoi');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleGenerateHeyGen = async (projectId) => {
    try {
      const res = await api.post(`/projects/${projectId}/generate-heygen`);
      toast.success('Video HeyGen lancee (2-5 min). Suivez dans Historique.', { duration: 5000 });
      setTimeout(() => navigate('/history'), 2000);
      loadStats();
      return res.data.video_id || res.data.id;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur HeyGen');
    }
  };

  const handlePublishAll = async (projectId) => {
    try {
      toast.info('Publication multi-plateforme en cours...');
      const res = await api.post(`/projects/${projectId}/publish-all`);
      const results = res.data.results;
      Object.entries(results).forEach(([platform, result]) => {
        if (result.status === 'published' || result.status === 'pending') {
          toast.success(`${platform}: publie !`);
        } else if (result.status === 'manual') {
          toast.info(`${platform}: publication manuelle requise`);
        } else {
          toast.error(`${platform}: erreur`);
        }
      });
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de publication');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Email verification banner */}
      {user?.auth_type === 'email' && user?.email_verified === false && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-[#FF9500]/10 border border-[#FF9500]/20" data-testid="email-verification-banner">
          <AlertTriangle className="w-5 h-5 text-[#FF9500] flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-white">Votre email n'est pas encore confirme.</p>
            <p className="text-xs text-white/40 mt-0.5">Verifiez votre boite de reception pour le lien de confirmation.</p>
          </div>
          <button
            onClick={handleResendConfirmation}
            disabled={resendingEmail}
            className="text-xs text-[#FF9500] hover:text-[#FF9500]/80 font-medium flex items-center gap-1 whitespace-nowrap disabled:opacity-50"
            data-testid="resend-confirmation-btn"
          >
            <Mail className="w-3 h-3" /> {resendingEmail ? 'Envoi...' : 'Renvoyer'}
          </button>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-white tracking-tight">
          Bonjour, {user?.name?.split(' ')[0] || 'Createur'}
        </h1>
        <p className="text-sm text-white/40 mt-1">Voici un apercu de votre activite</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-surface rounded-lg p-6" data-testid="stat-credits">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs tracking-[0.15em] uppercase text-white/40">Credits</span>
            <Zap className="w-4 h-4 text-[#FF3300]" />
          </div>
          <p className="font-heading text-3xl font-light text-white">
            {user?.plan_type === 'unlimited' ? '∞' : (stats?.credits_balance || 0)}
          </p>
          <p className="text-xs text-white/30 mt-1">
            {user?.plan_type === 'unlimited' ? 'Unlimited VIP' : (stats?.plan_type ? `Forfait ${stats.plan_type}` : 'Aucun forfait')}
          </p>
        </div>

        <div className="card-surface rounded-lg p-6" data-testid="stat-projects">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs tracking-[0.15em] uppercase text-white/40">Projets</span>
            <FolderPlus className="w-4 h-4 text-white/30" />
          </div>
          <p className="font-heading text-3xl font-light text-white">{stats?.projects_count || 0}</p>
          <p className="text-xs text-white/30 mt-1">chaines actives</p>
        </div>

        <div className="card-surface rounded-lg p-6" data-testid="stat-videos">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs tracking-[0.15em] uppercase text-white/40">Videos</span>
            <Video className="w-4 h-4 text-white/30" />
          </div>
          <p className="font-heading text-3xl font-light text-white">{stats?.videos_count || 0}</p>
          <p className="text-xs text-white/30 mt-1">videos generees</p>
        </div>

        <div className="card-surface rounded-lg p-6" data-testid="stat-plan">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs tracking-[0.15em] uppercase text-white/40">Forfait</span>
            <CreditCard className="w-4 h-4 text-white/30" />
          </div>
          <p className="font-heading text-2xl font-light text-white capitalize">{stats?.plan_type || 'Gratuit'}</p>
          <p className="text-xs text-white/30 mt-1">
            {stats?.plan_status === 'active' ? 'Actif' : 'Non souscrit'}
          </p>
        </div>
      </div>

      {/* Quick actions + Projects */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick actions */}
        <div className="card-surface rounded-lg p-6">
          <h2 className="font-heading text-lg font-medium text-white mb-4">Actions rapides</h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/projects/new')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm transition-all"
              data-testid="quick-new-project"
            >
              <FolderPlus className="w-4 h-4 text-[#FF3300]" />
              Nouveau projet
              <ArrowRight className="w-4 h-4 ml-auto text-white/30" />
            </button>
            {!stats?.plan_type && (
              <button
                onClick={() => navigate('/pricing')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#FF3300]/10 hover:bg-[#FF3300]/20 text-[#FF3300] text-sm transition-all"
                data-testid="quick-subscribe"
              >
                <CreditCard className="w-4 h-4" />
                Souscrire un forfait
                <ArrowRight className="w-4 h-4 ml-auto opacity-60" />
              </button>
            )}
            <button
              onClick={() => navigate('/history')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm transition-all"
              data-testid="quick-history"
            >
              <Clock className="w-4 h-4 text-white/40" />
              Voir l'historique
              <ArrowRight className="w-4 h-4 ml-auto text-white/30" />
            </button>
          </div>
        </div>

        {/* Projects list */}
        <div className="lg:col-span-2 card-surface rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-medium text-white">Mes Projets</h2>
            <button
              onClick={() => navigate('/projects/new')}
              className="text-xs text-[#FF3300] hover:text-[#FF3300]/80 transition-colors"
              data-testid="view-all-projects"
            >
              + Nouveau
            </button>
          </div>
          {!stats?.projects?.length ? (
            <div className="text-center py-12">
              <FolderPlus className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-sm text-white/30">Aucun projet pour le moment</p>
              <button
                onClick={() => navigate('/projects/new')}
                className="mt-4 text-sm text-[#FF3300] hover:underline"
              >
                Creer votre premier projet
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.projects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 transition-all cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                  data-testid={`project-item-${project.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{project.name}</p>
                    <p className="text-xs text-white/30 mt-1">
                      {project.niche || 'Pas de niche'} &middot; {project.videos_generated || 0} videos
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(project.youtube_connected || project.instagram_token === '***configure***' || project.x_api_key === '***configure***') && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePublishAll(project.id); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600/10 text-blue-400 text-xs font-medium hover:bg-blue-600/20 transition-all"
                        data-testid={`publish-all-btn-${project.id}`}
                      >
                        <Upload className="w-3 h-3" /> Publier
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleGenerateHeyGen(project.id); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-600/10 text-purple-400 text-xs font-medium hover:bg-purple-600/20 transition-all"
                      data-testid={`generate-heygen-btn-${project.id}`}
                    >
                      <UserCircle className="w-3 h-3" /> Avatar
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleGenerate(project.id); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FF3300]/10 text-[#FF3300] text-xs font-medium hover:bg-[#FF3300]/20 transition-all"
                      data-testid={`generate-btn-${project.id}`}
                    >
                      <Play className="w-3 h-3" /> Rapide
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent videos */}
      {stats?.recent_videos?.length > 0 && (
        <div className="card-surface rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-medium text-white">Videos recentes</h2>
            <button
              onClick={() => navigate('/history')}
              className="text-xs text-white/40 hover:text-white transition-colors"
            >
              Voir tout
            </button>
          </div>
          <div className="space-y-3">
            {stats.recent_videos.map((video) => (
              <div key={video.id} className="flex items-center gap-4 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Video className="w-5 h-5 text-white/20" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{video.title}</p>
                  <p className="text-xs text-white/30 mt-0.5">{video.project_name || 'Projet'}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  video.status === 'completed' ? 'badge-success' :
                  video.status === 'failed' ? 'badge-error' : 'badge-pending'
                }`}>
                  {video.status === 'completed' ? 'Termine' :
                   video.status === 'failed' ? 'Echoue' : 'En cours'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
